/**
 * Skills API client — companion to playbooks.js.
 *
 * Endpoints (see app/routers/skills.py):
 *   GET    /bot/api/skills/
 *   POST   /bot/api/skills/
 *   GET    /bot/api/skills/{id}
 *   PATCH  /bot/api/skills/{id}
 *   POST   /bot/api/skills/{id}/activate
 *   POST   /bot/api/skills/{id}/deactivate
 *   DELETE /bot/api/skills/{id}
 *
 *   GET    /bot/api/skill-variables/
 *   POST   /bot/api/skill-variables/
 *   PATCH  /bot/api/skill-variables/{id}
 *   DELETE /bot/api/skill-variables/{id}
 *
 *   GET    /bot/api/skill-tool-bindings/
 *   POST   /bot/api/skill-tool-bindings/
 *   PATCH  /bot/api/skill-tool-bindings/{id}
 *   DELETE /bot/api/skill-tool-bindings/{id}
 */

import { getAuthHeaders } from './auth';
import { getBaseUrl, parseJson, buildUrl } from './utils';

const SKILLS_PATH = 'bot/api/skills/';
const BINDINGS_PATH = 'bot/api/skill-tool-bindings/';
const VARIABLES_PATH = 'bot/api/skill-variables/';

function skillUrl(path = '') {
  return buildUrl(getBaseUrl(), SKILLS_PATH + path);
}
function bindingUrl(path = '') {
  return buildUrl(getBaseUrl(), BINDINGS_PATH + path);
}
function variableUrl(path = '') {
  return buildUrl(getBaseUrl(), VARIABLES_PATH + path);
}

async function handle(res) {
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    let detail = data.detail;
    let detailMessage = null;
    if (detail && typeof detail === 'object') {
      detailMessage = detail.message || null;
    } else if (typeof detail === 'string') {
      detailMessage = detail;
      detail = null;
    }
    const message =
      data.message || detailMessage || `Request failed (${res.status})`;
    const err = new Error(message);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return parseJson(res);
}

// ---- Skills CRUD ---------------------------------------------------------

export async function listSkills() {
  const res = await fetch(skillUrl(), {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

/**
 * Per-skill invocation counts, keyed by skill id (as a string).
 *
 * Aggregated server-side off `skill_sessions`, which already stores one row
 * per invocation — there is no separate analytics table. Skills that have
 * never run are ABSENT from the map rather than present with zeros, so
 * callers must default them.
 *
 * @param {number} days window in days; 0 means all time. Default 30.
 */
export async function getSkillStats(days = 30) {
  const res = await fetch(skillUrl(`stats?days=${encodeURIComponent(days)}`), {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

export async function getSkill(id) {
  const res = await fetch(skillUrl(String(id)), {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

/**
 * Dry-run validation — parses the markdown, resolves every tool named in
 * `tools:`, and checks {{VARIABLES}}. Writes nothing.
 *
 * Returns { valid, name, errors[], warnings[], tools[],
 *           variables_referenced[], variables_missing[] }
 * where each finding is { code, message, field? }.
 */
export async function validateSkill({ source_md }) {
  const res = await fetch(skillUrl('validate'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ source_md }),
  });
  return handle(res);
}

export async function createSkill({ source_md }) {
  const res = await fetch(skillUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ source_md }),
  });
  return handle(res);
}

export async function updateSkill(id, { source_md }) {
  const res = await fetch(skillUrl(String(id)), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ source_md }),
  });
  return handle(res);
}

export async function activateSkill(id) {
  const res = await fetch(skillUrl(`${id}/activate`), {
    method: 'POST',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

// Sandbox: the skill only engages for Test Bot chats (docs/SKILL_SANDBOX.md).
// Going live from sandbox is the same activateSkill call.
export async function sandboxSkill(id) {
  const res = await fetch(skillUrl(`${id}/sandbox`), {
    method: 'POST',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

export async function deactivateSkill(id) {
  const res = await fetch(skillUrl(`${id}/deactivate`), {
    method: 'POST',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

export async function deleteSkill(id) {
  const res = await fetch(skillUrl(String(id)), {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

// ---- Skill tool bindings CRUD --------------------------------------------

export async function listBindings() {
  const res = await fetch(bindingUrl(), {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

export async function createBinding(payload) {
  const res = await fetch(bindingUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  return handle(res);
}

export async function updateBinding(id, patch) {
  const res = await fetch(bindingUrl(String(id)), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(patch),
  });
  return handle(res);
}

export async function deleteBinding(id) {
  const res = await fetch(bindingUrl(String(id)), {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

// ---- Skill variables CRUD -------------------------------------------------
//
// Encrypted per-merchant config referenced as {{NAME}} from skill bodies
// and tool bindings. A SECRET variable's value is write-only: the API
// never returns it, so the form shows a "set" state rather than a value.

export async function listVariables() {
  const res = await fetch(variableUrl(), {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

export async function createVariable(payload) {
  const res = await fetch(variableUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  return handle(res);
}

export async function updateVariable(id, patch) {
  const res = await fetch(variableUrl(String(id)), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(patch),
  });
  return handle(res);
}

export async function deleteVariable(id) {
  const res = await fetch(variableUrl(String(id)), {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

// ---- Skill Library ---------------------------------------------------------
//
// GET  /bot/api/skill-library/                 ready-made skills for this
//                                              store's platform (empty when
//                                              none apply)
// POST /bot/api/skill-library/{name}/install   copy one in as a draft
//
// See ac-fastapi-staging-main/docs/SKILL_LIBRARY.md.

const LIBRARY_PATH = 'bot/api/skill-library/';

function libraryUrl(path = '') {
  return buildUrl(getBaseUrl(), LIBRARY_PATH + path);
}

export async function listLibrarySkills() {
  const res = await fetch(libraryUrl(), {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

/** Returns the merchant's draft copy (or their existing copy if already added). */
export async function installLibrarySkill(name) {
  const res = await fetch(libraryUrl(`${encodeURIComponent(name)}/install`), {
    method: 'POST',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

// ---- Skill Builder ---------------------------------------------------------
//
// POST /bot/api/skill-builder/        start a build from a plain-language
//                                     requirement (returns the queued job)
// GET  /bot/api/skill-builder/{id}    poll one job
// GET  /bot/api/skill-builder/        recent jobs for this company
//
// See ac-fastapi-staging-main/docs/SKILL_BUILDER_SETUP.md.

const BUILDER_PATH = 'bot/api/skill-builder/';

function builderUrl(path = '') {
  return buildUrl(getBaseUrl(), BUILDER_PATH + path);
}

/**
 * Send a message. Omit `thread_id` to start a conversation; pass the one
 * from an earlier turn to continue it. Returns the queued turn.
 */
export async function startSkillBuild({ requirement, thread_id: threadId }) {
  const res = await fetch(builderUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(threadId ? { requirement, thread_id: threadId } : { requirement }),
  });
  return handle(res);
}

export async function getSkillBuild(id) {
  const res = await fetch(builderUrl(String(id)), {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}

/**
 * Recent turns, newest first. With `threadId`, one conversation in
 * oldest-first (reading) order instead.
 */
export async function listSkillBuilds(limit = 5, threadId = null) {
  const query = threadId
    ? `?limit=${encodeURIComponent(limit)}&thread_id=${encodeURIComponent(threadId)}`
    : `?limit=${encodeURIComponent(limit)}`;
  const res = await fetch(builderUrl(query), {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return handle(res);
}
